import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuTrash2,
    LuBuilding2, LuUsers, LuUnlink, LuTriangleAlert, LuUpload, LuDownload, LuHistory,
} from 'react-icons/lu';
import { exportarListaCsv } from '../services/exportarCsv';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import ImportarPlanilhaPanel from '../components/ImportarPlanilhaPanel';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './OrganizationsPage.css';
import {
    OrganizationService, formatarCnpj, mascararCnpj, somenteDigitos,
    type Organization, type OrganizationPayload, type DuplicidadeOrganizacao,
} from '../services/OrganizationService';
import { ContactService, type Contact } from '../services/ContactService';
import { mascararCep, manterSeMesmaCidade } from '../services/CepService';
import { useBuscaCep } from '../hooks/useBuscaCep';

const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';
type Aba = 'dados' | 'pessoas' | 'historico';

interface Rascunho {
    organizacaoId?: number;
    nome: string; nomeFantasia: string; cnpj: string; segmento: string;
    site: string; email: string; telefone: string;
    cep: string; logradouro: string; numero: string; complemento: string;
    bairro: string; cidade: string; estado: string; observacao: string;
}

const VAZIO: Rascunho = {
    nome: '', nomeFantasia: '', cnpj: '', segmento: '', site: '', email: '', telefone: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
    observacao: '',
};

const paraRascunho = (o: Organization): Rascunho => ({
    organizacaoId: o.organizacaoId,
    nome: o.nome ?? '', nomeFantasia: o.nomeFantasia ?? '', cnpj: mascararCnpj(o.cnpj ?? ''),
    segmento: o.segmento ?? '', site: o.site ?? '', email: o.email ?? '', telefone: o.telefone ?? '',
    cep: mascararCep(o.cep ?? ''), logradouro: o.logradouro ?? '', numero: o.numero ?? '',
    complemento: o.complemento ?? '', bairro: o.bairro ?? '', cidade: o.cidade ?? '',
    estado: o.estado ?? '', observacao: o.observacao ?? '',
});

/** Iniciais da organização: duas letras das duas primeiras palavras que valem. */
const iniciais = (nome: string) => {
    const ignorar = ['de', 'da', 'do', 'das', 'dos', 'e'];
    const palavras = nome.trim().split(/\s+/).filter(p => !ignorar.includes(p.toLowerCase()));
    return palavras.slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
};

const iniciaisPessoa = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const cidadeUf = (o: Organization) => [o.cidade, o.estado].filter(Boolean).join('/');

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

/* ─── Filtro em pill com popover ──────────────────────────────────
   Mesmo controle da tela de Contatos. Vive aqui porque lá ele também
   é local; unificar os dois é tarefa da limpeza do ListKit, não desta
   entrega. */

interface FiltroProps {
    rotulo: string;
    valor: string | null;
    opcoes: { valor: string; total: number }[];
    onChange: (v: string | null) => void;
}

const FiltroPill = ({ rotulo, valor, opcoes, onChange }: FiltroProps) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    if (opcoes.length === 0) return null;

    return (
        <div className="lk-filter" ref={ref}>
            <button
                type="button"
                className={`lk-pill ${valor ? 'is-on' : ''}`}
                aria-expanded={aberto}
                onClick={() => setAberto(a => !a)}
            >
                {rotulo}{valor ? `: ${valor}` : ''}
            </button>
            {aberto && (
                <div className="lk-pop">
                    <button type="button" className={`lk-opt ${!valor ? 'is-on' : ''}`}
                        onClick={() => { onChange(null); setAberto(false); }}>
                        Todos
                    </button>
                    {opcoes.map(o => (
                        <button key={o.valor} type="button"
                            className={`lk-opt ${valor === o.valor ? 'is-on' : ''}`}
                            onClick={() => { onChange(o.valor); setAberto(false); }}>
                            {o.valor}<span className="lk-opt-n">{o.total}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ─── Tela ────────────────────────────────────────────────────── */

const OrganizationsPage = () => {
    const celular = useCelular();
    const [organizacoes, setOrganizacoes] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Importar, exportar e excluir: mesma chave de Contatos (`contatos.gerenciar`)
       — a API recusa importar e excluir sem ela. */
    const podeGerenciar = temPermissao(PERMISSOES.contatosGerenciar);
    const [importando, setImportando] = useState(false);

    /* Recorte de trabalho: abrir uma organizacao, voltar e reencontrar a mesma
       lista. Vive na sessao da aba. */
    const [recorte, gravarRecorte] = useRecorte('org:recorte', {
        busca: '', segmento: null as string | null, cidade: null as string | null,
        porPagina: POR_PAGINA[0], pagina: 1,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [segmento, setSegmento] = useState<string | null>(recorte.segmento);
    const [cidade, setCidade] = useState<string | null>(recorte.cidade);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    const [pagina, setPagina] = useState(recorte.pagina);
    useEffect(() => {
        gravarRecorte({ busca, segmento, cidade, porPagina, pagina });
    }, [busca, segmento, cidade, porPagina, pagina, gravarRecorte]);

    const [aberto, setAberto] = useState<Organization | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [aba, setAba] = useState<Aba>('dados');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [erros, setErros] = useState<Record<string, string>>({});
    const [salvando, setSalvando] = useState(false);

    // Pessoas da organização aberta — carregadas só quando o painel abre.
    const [pessoas, setPessoas] = useState<Contact[]>([]);
    const [carregandoPessoas, setCarregandoPessoas] = useState(false);

    // Vincular: busca sobre TODOS os contatos, para poder separar os que já
    // têm organização dos que não têm.
    const [todosContatos, setTodosContatos] = useState<Contact[]>([]);
    const [buscaPessoa, setBuscaPessoa] = useState('');
    const [vinculando, setVinculando] = useState(false);

    // Duplicidade: o aviso aparece enquanto digita, não no salvar.
    const [duplicidade, setDuplicidade] = useState<DuplicidadeOrganizacao | null>(null);

    const ehNova = modo === 'editar' && !rascunho.organizacaoId;

    /* CEP completo preenche o resto. Número e complemento nunca: a BrasilAPI
       não sabe, e o foco vai para o primeiro campo que ficou para a pessoa. */
    const cep = useBuscaCep(e => {
        setRascunho(r => {
            const manter = manterSeMesmaCidade(e, r);
            return {
                ...r,
                logradouro: e.logradouro ?? (manter ? r.logradouro : ''),
                bairro: e.bairro ?? (manter ? r.bairro : ''),
                cidade: e.cidade,
                estado: e.estado,
            };
        });
        return e.logradouro ? 'og-num' : 'og-rua';
    });

    /* ─── Carga ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const resposta = await OrganizationService.getAll();
            setOrganizacoes(resposta.dados ?? []);
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível buscar as organizações.'));
            setOrganizacoes([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    /* O painel do contato aponta para cá com ?abrir=<id>. Abre uma vez, e
       limpa o parâmetro para o F5 não reabrir o painel sozinho. */
    useEffect(() => {
        const alvo = Number(new URLSearchParams(window.location.search).get('abrir'));
        if (!alvo || organizacoes.length === 0) return;

        const achada = organizacoes.find(o => o.organizacaoId === alvo);
        if (achada) abrirVer(achada);
        window.history.replaceState(null, '', window.location.pathname);
    }, [organizacoes]);

    // A lista de contatos alimenta o campo de vincular. Uma vez só.
    useEffect(() => {
        ContactService.getAll()
            .then(r => setTodosContatos(r.dados ?? []))
            .catch(() => setTodosContatos([]));
    }, []);

    const carregarPessoas = useCallback(async (organizacaoId: number) => {
        setCarregandoPessoas(true);
        try {
            setPessoas(await OrganizationService.getContacts(organizacaoId));
        } catch {
            setPessoas([]);
        } finally {
            setCarregandoPessoas(false);
        }
    }, []);

    useEffect(() => {
        if (!aberto) { setPessoas([]); return; }
        carregarPessoas(aberto.organizacaoId);
    }, [aberto, carregarPessoas]);

    /* ─── Duplicidade ────────────────────────────────────────── */

    useEffect(() => {
        if (modo !== 'editar') { setDuplicidade(null); return; }

        const nome = rascunho.nome.trim();
        const cnpj = somenteDigitos(rascunho.cnpj);
        if (nome.length < 3 && !cnpj) { setDuplicidade(null); return; }

        // Espera a digitação parar: sem isso é uma consulta por tecla.
        const id = window.setTimeout(() => {
            OrganizationService
                .verificarDuplicidade(nome, cnpj, rascunho.organizacaoId)
                .then(setDuplicidade)
                .catch(() => setDuplicidade(null));
        }, 400);

        return () => window.clearTimeout(id);
    }, [modo, rascunho.nome, rascunho.cnpj, rascunho.organizacaoId]);

    /* ─── Filtros ────────────────────────────────────────────── */

    const contar = (pegar: (o: Organization) => string | null | undefined) => {
        const mapa = new Map<string, number>();
        organizacoes.forEach(o => {
            const v = (pegar(o) ?? '').trim();
            if (v) mapa.set(v, (mapa.get(v) ?? 0) + 1);
        });
        return [...mapa.entries()]
            .map(([valor, total]) => ({ valor, total }))
            .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor));
    };

    const opcoesSegmento = useMemo(() => contar(o => o.segmento), [organizacoes]);
    const opcoesCidade = useMemo(() => contar(o => cidadeUf(o)), [organizacoes]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const digitos = termo.replace(/\D/g, '');
        return organizacoes.filter(o => {
            if (segmento && (o.segmento ?? '').trim() !== segmento) return false;
            if (cidade && cidadeUf(o) !== cidade) return false;
            if (!termo) return true;
            return (
                o.nome.toLowerCase().includes(termo)
                || (o.nomeFantasia ?? '').toLowerCase().includes(termo)
                || (digitos.length > 0 && (o.cnpj ?? '').includes(digitos))
            );
        });
    }, [organizacoes, busca, segmento, cidade]);

    /* Mesma regra de Contatos: o que o filtro deixou, o cadastro inteiro, só o
       administrador. CNPJ e CEP saem pontuados — só dígitos o Excel vira número
       e come o zero da frente. */
    const exportar = () => exportarListaCsv('organizacoes', filtradas, [
        { rotulo: 'Razão social', valor: o => o.nome },
        { rotulo: 'Nome fantasia', valor: o => o.nomeFantasia },
        { rotulo: 'CNPJ', valor: o => o.cnpj && formatarCnpj(o.cnpj) },
        { rotulo: 'Segmento', valor: o => o.segmento },
        { rotulo: 'Telefone', valor: o => o.telefone, tipo: 'telefone' },
        { rotulo: 'E-mail', valor: o => o.email },
        { rotulo: 'Site', valor: o => o.site },
        { rotulo: 'CEP', valor: o => o.cep && mascararCep(o.cep) },
        { rotulo: 'Rua', valor: o => o.logradouro },
        { rotulo: 'Número', valor: o => o.numero },
        { rotulo: 'Complemento', valor: o => o.complemento },
        { rotulo: 'Bairro', valor: o => o.bairro },
        { rotulo: 'Cidade', valor: o => o.cidade },
        { rotulo: 'UF', valor: o => o.estado },
        { rotulo: 'Pessoas', valor: o => o.quantidadePessoas, tipo: 'numero' },
        { rotulo: 'Observação', valor: o => o.observacao },
        { rotulo: 'Cadastro', valor: o => o.dataCriacao, tipo: 'data' },
    ]);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, segmento, cidade, porPagina]);

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtradas.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (o: Organization) => {
        setAberto(o);
        setModo('ver');
        setAba('dados');
        setErros({});
    };

    const abrirEdicao = (o: Organization) => {
        setAberto(o);
        setRascunho(paraRascunho(o));
        setModo('editar');
        setErros({});
        cep.reiniciar();
    };

    /* A paleta do Ctrl+K pediu "Nova organização": abre o formulário direto. */
    useAbrirNovo(() => abrirNova());

    const abrirNova = () => {
        setAberto(null);
        setRascunho(VAZIO);
        setModo('editar');
        setAba('dados');
        setErros({});
        setDuplicidade(null);
        cep.reiniciar();
    };

    const fechar = () => {
        setAberto(null);
        setModo('ver');
        setRascunho(VAZIO);
        setErros({});
        setDuplicidade(null);
        setBuscaPessoa('');
        cep.reiniciar();
    };

    const campo = (chave: keyof Rascunho) => ({
        value: rascunho[chave] as string,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const valor = chave === 'cnpj' ? mascararCnpj(e.target.value)
                : chave === 'cep' ? mascararCep(e.target.value)
                : e.target.value;
            setRascunho(r => ({ ...r, [chave]: valor }));
            setErros(x => ({ ...x, [chave]: '' }));
            if (chave === 'cep') cep.aoDigitar(valor);
        },
    });

    const validar = () => {
        const novos: Record<string, string> = {};
        if (!rascunho.nome.trim()) novos.nome = 'Informe a razão social.';

        const digitos = somenteDigitos(rascunho.cnpj);
        if (digitos && digitos.length !== 14) novos.cnpj = 'O CNPJ tem 14 dígitos.';
        if (rascunho.email.trim() && !/^\S+@\S+\.\S+$/.test(rascunho.email.trim())) {
            novos.email = 'E-mail inválido.';
        }
        setErros(novos);
        return Object.keys(novos).length === 0;
    };

    const payload = (): OrganizationPayload => ({
        nome: rascunho.nome.trim(),
        nomeFantasia: rascunho.nomeFantasia.trim() || null,
        cnpj: somenteDigitos(rascunho.cnpj),
        site: rascunho.site.trim() || null,
        email: rascunho.email.trim() || null,
        telefone: rascunho.telefone.trim() || null,
        segmento: rascunho.segmento.trim() || null,
        cep: rascunho.cep.trim() || null,
        logradouro: rascunho.logradouro.trim() || null,
        numero: rascunho.numero.trim() || null,
        complemento: rascunho.complemento.trim() || null,
        bairro: rascunho.bairro.trim() || null,
        cidade: rascunho.cidade.trim() || null,
        estado: rascunho.estado.trim() || null,
        observacao: rascunho.observacao.trim() || null,
    });

    const salvar = async () => {
        if (!validar()) return;
        setSalvando(true);
        try {
            if (rascunho.organizacaoId) {
                await OrganizationService.update(rascunho.organizacaoId, payload());
                aviso.ok('Organização atualizada.');
            } else {
                await OrganizationService.create(payload());
                aviso.ok('Organização criada.');
            }
            await carregar();
            fechar();
        } catch (e) {
            // A mensagem da API é a que explica o bloqueio por CNPJ.
            aviso.erro(textoDoErro(e, 'Não foi possível salvar a organização.'));
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Excluir vem de dois lugares: a lixeira da LINHA manda o alvo, o rodapé do
     * painel usa o que está aberto. Sem o parâmetro, a lixeira da linha apagaria
     * o registro errado — ou nenhum.
     */
    const excluir = async (alvo?: Organization) => {
        const id = alvo?.organizacaoId ?? rascunho.organizacaoId;
        if (!id) return;

        const nome = alvo?.nome || rascunho.nome || 'esta organização';
        const pessoas = alvo?.quantidadePessoas ?? aberto?.quantidadePessoas ?? 0;
        const sobrePessoas = pessoas > 0
            ? ` As ${pessoas} pessoas vinculadas continuam cadastradas, sem organização.`
            : '';
        if (!window.confirm(`Excluir ${nome}?${sobrePessoas}`)) return;

        setSalvando(true);
        try {
            await OrganizationService.remove(id);
            aviso.ok('Organização removida.');
            await carregar();
            fechar();
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível remover a organização.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── Pessoas ────────────────────────────────────────────── */

    const candidatos = useMemo(() => {
        const termo = buscaPessoa.trim().toLowerCase();
        if (termo.length < 2) return { livres: [], ocupados: [] };

        const jaVinculados = new Set(pessoas.map(p => p.contatoId));
        const achados = todosContatos.filter(c =>
            !jaVinculados.has(c.contatoId)
            && ((c.nome ?? '').toLowerCase().includes(termo)
                || (c.telefone ?? '').includes(termo)),
        ).slice(0, 12);

        return {
            livres: achados.filter(c => !c.organizacaoId),
            ocupados: achados.filter(c => !!c.organizacaoId),
        };
    }, [buscaPessoa, todosContatos, pessoas]);

    const vincular = async (contato: Contact) => {
        if (!aberto) return;
        setVinculando(true);
        try {
            await OrganizationService.linkContact(aberto.organizacaoId, contato.contatoId);
            aviso.ok(`${contato.nome} vinculado.`);
            setBuscaPessoa('');
            await carregarPessoas(aberto.organizacaoId);
            await carregar();
            const atualizados = await ContactService.getAll();
            setTodosContatos(atualizados.dados ?? []);
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível vincular o contato.'));
        } finally {
            setVinculando(false);
        }
    };

    const desvincular = async (contato: Contact) => {
        if (!aberto) return;
        setVinculando(true);
        try {
            await OrganizationService.unlinkContact(contato.contatoId);
            aviso.ok(`${contato.nome} desvinculado.`);
            await carregarPessoas(aberto.organizacaoId);
            await carregar();
            const atualizados = await ContactService.getAll();
            setTodosContatos(atualizados.dados ?? []);
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível desvincular o contato.'));
        } finally {
            setVinculando(false);
        }
    };

    /* ─── Render ─────────────────────────────────────────────── */

    const COLUNAS = '1.3fr .9fr .85fr .85fr .55fr .7fr 78px';

    const bloqueado = !!duplicidade?.bloqueioPorCnpj;
    const parecidas = duplicidade?.avisoPorNome ?? [];

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {organizacoes.length === 0 ? 'Nenhuma organização cadastrada' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {organizacoes.length === 0
                    ? 'Cadastre as empresas com quem você negocia e vincule as pessoas que já estão em Contatos.'
                    : 'Ajuste a busca ou limpe os filtros para ver os outros registros.'}
            </p>
            {organizacoes.length === 0
                ? <div className="ip-blank-actions">
                    {podeGerenciar && (
                        <button type="button" className="lk-btn" onClick={() => setImportando(true)}>
                            <LuUpload size={14} /> Importar planilha
                        </button>
                    )}
                    <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                        <LuPlus size={14} /> Nova organização
                    </button>
                  </div>
                : <button type="button" className="lk-btn"
                    onClick={() => { setBusca(''); setSegmento(null); setCidade(null); }}>
                    Limpar filtros
                  </button>}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />

            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}
            </span>

            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Organizações"
                subtitle="As empresas com quem você negocia — e quem trabalha em cada uma"
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && organizacoes.length === 0 ? undefined :
                    <div className="lk-actions">
                        {podeGerenciar && (
                            <button type="button" className="lk-btn" onClick={() => setImportando(true)}>
                                <LuUpload size={14} /> Importar planilha
                            </button>
                        )}
                        {podeGerenciar && (
                            <button type="button" className="lk-btn" onClick={exportar} disabled={isLoading}>
                                <LuDownload size={14} /> Exportar
                            </button>
                        )}
                        <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                            <LuPlus size={14} /> Nova organização
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input
                            type="search"
                            placeholder="Buscar por nome ou CNPJ…"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                        />
                    </label>

                    <FiltroPill rotulo="Segmento" valor={segmento} opcoes={opcoesSegmento} onChange={setSegmento} />
                    <FiltroPill rotulo="Cidade" valor={cidade} opcoes={opcoesCidade} onChange={setCidade} />

                    <span className="lk-count">
                        {organizacoes.length} {organizacoes.length === 1 ? 'organização' : 'organizações'}
                        {filtradas.length !== organizacoes.length && ` · ${filtradas.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade de 7 colunas não cabe em 358px, então a linha
                    vira cartão — quem é a empresa, como ela se identifica e quantas
                    pessoas tem. Segmento, endereço e cadastro ficam no painel, um
                    toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(o => (
                                    <ListCard
                                        key={o.organizacaoId}
                                        avatar={<span className="lk-ava" aria-hidden="true">{iniciais(o.nomeFantasia || o.nome)}</span>}
                                        titulo={o.nomeFantasia || o.nome}
                                        apoio={o.cnpj ? formatarCnpj(o.cnpj) : (cidadeUf(o) || 'sem CNPJ')}
                                        estado={
                                            o.quantidadePessoas > 0
                                                ? <span className="og-pessoas"><LuUsers size={12} />{o.quantidadePessoas}</span>
                                                : <span>sem pessoas vinculadas</span>
                                        }
                                        selected={aberto?.organizacaoId === o.organizacaoId}
                                        label={`Abrir ${o.nome}`}
                                        onOpen={() => abrirVer(o)}
                                        acoes={podeGerenciar ? (
                                            <RowAction icon={<LuTrash2 size={15} />} label={`Excluir ${o.nome}`} danger
                                                onClick={() => excluir(o)} />
                                        ) : undefined}
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtradas.length === 0 && listaVazia}
                        {!isLoading && filtradas.length > 0 && rodape}
                    </>
                ) : (
                /* Fragmento porque aqui o rodapé é IRMÃO da moldura, não filho
                   dela como em Contatos: envolver os dois num `div` mudaria o
                   desenho do computador, que tem de ficar igual. */
                <>
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Organização</div>
                            <div className="lk-head">CNPJ</div>
                            <div className="lk-head">Segmento</div>
                            <div className="lk-head">Cidade/UF</div>
                            <div className="lk-head">Pessoas</div>
                            <div className="lk-head">Cadastro</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>

                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(o => (
                                    /* Sem `onOpen`: nas telas de cadastro quem abre o registro é o
                                       olho da coluna de ações. Ver padrao-de-tela.md. */
                                    <ListRow
                                        key={o.organizacaoId}
                                        columns={COLUNAS}
                                        selected={aberto?.organizacaoId === o.organizacaoId}
                                    >
                                        {/* A linha leva o nome CURTO; a razão social fica na ficha.
                                            Decisão do §6 do desenho: um campo só obrigaria escolher
                                            entre a lista feia e o documento errado. */}
                                        <span className="lk-cell lk-strong"><span>{o.nomeFantasia || o.nome}</span></span>

                                        <span className={`lk-cell ${o.cnpj ? 'og-mono' : 'lk-empty-value'}`}>
                                            <span>{o.cnpj ? formatarCnpj(o.cnpj) : 'sem CNPJ'}</span>
                                        </span>

                                        <span className={`lk-cell ${o.segmento ? 'lk-muted' : 'lk-empty-value'}`}>
                                            <span>{o.segmento || 'sem segmento'}</span>
                                        </span>

                                        <span className={`lk-cell ${cidadeUf(o) ? 'lk-muted' : 'lk-empty-value'}`}>
                                            <span>{cidadeUf(o) || '—'}</span>
                                        </span>

                                        {/* Zero vira travessão: ficha recém-criada não é problema. */}
                                        <span className="lk-cell">
                                            {o.quantidadePessoas > 0
                                                ? <span className="og-pessoas"><LuUsers size={12} />{o.quantidadePessoas}</span>
                                                : <span className="lk-empty-value">—</span>}
                                        </span>

                                        <span className="lk-cell lk-muted"><span>{dataCurta(o.dataCriacao)}</span></span>

                                        <RowActions>
                                            <RowAction icon={<LuEye size={14} />} label={`Ver ${o.nome}`}
                                                onClick={() => abrirVer(o)} />
                                            {podeGerenciar && (
                                                <RowAction icon={<LuTrash2 size={14} />} label={`Excluir ${o.nome}`} danger
                                                    onClick={() => excluir(o)} />
                                            )}
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>

                    {/* Dentro da moldura, como nas outras listagens: fora dela o
                        aviso caía no pé da tela, abaixo do cartão vazio. */}
                    {!isLoading && filtradas.length === 0 && listaVazia}
                </div>

                {!isLoading && filtradas.length > 0 && rodape}
                </>
                )}
            </div>

            <RecordPanel
                open={!!aberto || modo === 'editar'}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.nome || 'Nova') : (aberto?.nome ?? ''))}
                squareAvatar
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNova ? 'Nova organização' : 'Organização'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto?.dataCriacao
                    ? `Organização · cadastrada em ${dataCurta(aberto.dataCriacao)}`
                    : 'Organização'}
                editing={modo === 'editar'}
                /* As abas só existem no modo ver: o formulário é um assunto só.
                   A terceira aba (Oportunidades) entra na fase 2 — aba vazia
                   seria promessa. Ver docs/propostas/organizacoes-fase-1.html §3. */
                tabs={modo === 'ver' && aberto ? [
                    { id: 'dados', label: 'Dados', icon: <LuBuilding2 size={13} /> },
                    { id: 'pessoas', label: 'Pessoas', icon: <LuUsers size={13} />, badge: pessoas.length || undefined },
                    /* Histórico: painel com abas ganha uma aba (docs/propostas/historico.html). */
                    { id: 'historico', label: 'Histórico', icon: <LuHistory size={13} /> },
                ] : undefined}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                /* Sem `actions`: em painel com abas o Editar mora DENTRO da aba
                   Dados, senão ele fica acima da barra de abas e parece valer
                   para as duas. Ver padrao-de-tela.md. */
                onBack={ehNova ? undefined : () => setModo('ver')}
                footer={
                    <>
                        {!ehNova && podeGerenciar && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Excluir organização
                            </button>
                        )}
                        <button type="button" className="rp-btn" onClick={fechar} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar}
                            disabled={salvando || bloqueado}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && aba === 'dados' && (
                    <>
                        <div className="rp-actions is-inline">
                            <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                                <LuPencil size={13} /> Editar
                            </button>
                        </div>

                        <PanelSection title="Organização">
                            <div className="rp-kv">
                                <span className="rp-k">Razão social</span>
                                <span className="rp-v">{aberto.nome}</span>

                                <span className="rp-k">Nome fantasia</span>
                                {aberto.nomeFantasia
                                    ? <span className="rp-v">{aberto.nomeFantasia}</span>
                                    : <span className="rp-v is-empty">não informado</span>}

                                <span className="rp-k">CNPJ</span>
                                {aberto.cnpj
                                    ? <span className="rp-v og-mono">{formatarCnpj(aberto.cnpj)}</span>
                                    : <span className="rp-v is-empty">sem CNPJ</span>}

                                <span className="rp-k">Segmento</span>
                                {aberto.segmento
                                    ? <span className="rp-v"><span className="rp-tag">{aberto.segmento}</span></span>
                                    : <span className="rp-v is-empty">não informado</span>}

                                <span className="rp-k">Site</span>
                                {aberto.site
                                    ? <span className="rp-v">{aberto.site}</span>
                                    : <span className="rp-v is-empty">não informado</span>}

                                <span className="rp-k">Telefone</span>
                                {aberto.telefone
                                    ? <span className="rp-v">{aberto.telefone}</span>
                                    : <span className="rp-v is-empty">não informado</span>}

                                <span className="rp-k">E-mail</span>
                                {aberto.email
                                    ? <span className="rp-v">{aberto.email}</span>
                                    : <span className="rp-v is-empty">não informado</span>}
                            </div>
                        </PanelSection>

                        <PanelSection title="Endereço">
                            {aberto.logradouro || aberto.cidade ? (
                                <div className="rp-kv">
                                    <span className="rp-k">CEP</span>
                                    {aberto.cep
                                        ? <span className="rp-v og-mono">{mascararCep(aberto.cep)}</span>
                                        : <span className="rp-v is-empty">não informado</span>}

                                    <span className="rp-k">Logradouro</span>
                                    <span className="rp-v">
                                        {[aberto.logradouro, aberto.numero].filter(Boolean).join(', ')}
                                        {aberto.complemento ? ` — ${aberto.complemento}` : ''}
                                    </span>

                                    <span className="rp-k">Bairro</span>
                                    {aberto.bairro
                                        ? <span className="rp-v">{aberto.bairro}</span>
                                        : <span className="rp-v is-empty">não informado</span>}

                                    <span className="rp-k">Cidade/UF</span>
                                    {cidadeUf(aberto)
                                        ? <span className="rp-v">{cidadeUf(aberto)}</span>
                                        : <span className="rp-v is-empty">não informada</span>}
                                </div>
                            ) : <p className="rp-none">Sem endereço cadastrado.</p>}
                        </PanelSection>

                        {aberto.observacao && (
                            <PanelSection title="Observação">
                                <p className="og-obs">{aberto.observacao}</p>
                            </PanelSection>
                        )}
                    </>
                )}

                {modo === 'ver' && aberto && aba === 'pessoas' && (
                    <PanelSection title={`Pessoas${pessoas.length ? ` · ${pessoas.length}` : ''}`}>
                        <label className="og-buscar">
                            <LuSearch size={13} />
                            <input
                                type="search"
                                placeholder="Buscar contato para vincular…"
                                value={buscaPessoa}
                                onChange={e => setBuscaPessoa(e.target.value)}
                                disabled={vinculando}
                            />
                        </label>

                        {buscaPessoa.trim().length >= 2 && (
                            <div className="og-cands">
                                {candidatos.livres.length === 0 && candidatos.ocupados.length === 0 && (
                                    <p className="og-cands-vazio">Nenhum contato encontrado com esse nome.</p>
                                )}

                                {candidatos.livres.length > 0 && (
                                    <>
                                        <p className="og-cands-titulo">Contatos sem organização</p>
                                        {candidatos.livres.map(c => (
                                            <button key={c.contatoId} type="button" className="og-cand"
                                                onClick={() => vincular(c)} disabled={vinculando}>
                                                <span className="og-cand-nome">{c.nome}</span>
                                                <span className="og-cand-info">{c.telefone || 'sem telefone'}</span>
                                            </button>
                                        ))}
                                    </>
                                )}

                                {/* Vincular quem já tem organização é TROCA, não soma:
                                    o grupo separado põe isso à vista antes do clique. */}
                                {candidatos.ocupados.length > 0 && (
                                    <>
                                        <p className="og-cands-titulo">Já em outra organização</p>
                                        {candidatos.ocupados.map(c => (
                                            <button key={c.contatoId} type="button" className="og-cand is-troca"
                                                onClick={() => vincular(c)} disabled={vinculando}>
                                                <span className="og-cand-nome">{c.nome}</span>
                                                <span className="og-cand-info">{c.organizacaoNome}</span>
                                            </button>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}

                        {carregandoPessoas
                            ? <p className="rp-none">Carregando…</p>
                            : pessoas.length === 0
                                ? <p className="rp-none">Nenhuma pessoa vinculada ainda.</p>
                                : pessoas.map(p => (
                                    <div key={p.contatoId} className="og-pessoa">
                                        <span className="og-pessoa-av">{iniciaisPessoa(p.nome ?? '')}</span>
                                        <span className="og-pessoa-nome">{p.nome}</span>
                                        <span className="og-pessoa-tel">{p.telefone || 'sem telefone'}</span>
                                        <button type="button" className="og-pessoa-x" title="Desvincular"
                                            aria-label={`Desvincular ${p.nome}`}
                                            onClick={() => desvincular(p)} disabled={vinculando}>
                                            <LuUnlink size={12} />
                                        </button>
                                    </div>
                                ))}
                    </PanelSection>
                )}

                {modo === 'editar' && (
                    <>
                        <PanelSection title="Organização">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="og-nome">Razão social</label>
                                <input id="og-nome" className={`rp-input ${erros.nome ? 'has-error' : ''}`}
                                    autoFocus {...campo('nome')} />
                                {erros.nome && <p className="rp-error">{erros.nome}</p>}

                                {/* Nome parecido AVISA e deixa seguir. Quem bloqueia é o CNPJ. */}
                                {parecidas.length > 0 && (
                                    <div className="og-dup">
                                        <LuTriangleAlert size={13} />
                                        <div>
                                            <b>Já existe "{parecidas[0].nome}"</b>
                                            {parecidas[0].quantidadePessoas > 0
                                                ? `, com ${parecidas[0].quantidadePessoas} ${parecidas[0].quantidadePessoas === 1 ? 'pessoa' : 'pessoas'} vinculada${parecidas[0].quantidadePessoas === 1 ? '' : 's'}.`
                                                : '.'} É a mesma?
                                            <div className="og-dup-acts">
                                                <button type="button" className="og-dup-b is-primary"
                                                    onClick={() => { const alvo = parecidas[0]; fechar(); abrirVer(alvo); }}>
                                                    Abrir a existente
                                                </button>
                                                <span className="og-dup-nota">ou continue — o cadastro segue liberado</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-fantasia">Nome fantasia</label>
                                    <input id="og-fantasia" className="rp-input" {...campo('nomeFantasia')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-cnpj">CNPJ</label>
                                    <input id="og-cnpj"
                                        className={`rp-input ${erros.cnpj || bloqueado ? 'has-error' : ''}`}
                                        placeholder="00.000.000/0000-00" inputMode="numeric" {...campo('cnpj')} />
                                    {erros.cnpj && <p className="rp-error">{erros.cnpj}</p>}
                                </div>
                            </div>

                            {/* CNPJ repetido BLOQUEIA: o índice único recusaria de qualquer forma. */}
                            {bloqueado && (
                                <div className="og-dup is-erro">
                                    <LuTriangleAlert size={13} />
                                    <div>
                                        Este CNPJ já é da <b>{duplicidade?.bloqueioPorCnpj?.nome}</b>.
                                        Duas organizações não podem ter o mesmo CNPJ.
                                        <div className="og-dup-acts">
                                            <button type="button" className="og-dup-b is-primary"
                                                onClick={() => {
                                                    const alvo = duplicidade?.bloqueioPorCnpj;
                                                    if (alvo) { fechar(); abrirVer(alvo); }
                                                }}>
                                                Abrir a existente
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-segmento">Segmento</label>
                                    <input id="og-segmento" className="rp-input" placeholder="Ex: Construção civil"
                                        {...campo('segmento')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-tel">Telefone</label>
                                    <input id="og-tel" className="rp-input" {...campo('telefone')} />
                                </div>
                            </div>

                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-site">Site</label>
                                    <input id="og-site" className="rp-input" placeholder="empresa.com.br" {...campo('site')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-mail">E-mail</label>
                                    <input id="og-mail" type="email"
                                        className={`rp-input ${erros.email ? 'has-error' : ''}`}
                                        placeholder="contato@empresa.com.br" {...campo('email')} />
                                    {erros.email && <p className="rp-error">{erros.email}</p>}
                                </div>
                            </div>
                        </PanelSection>

                        <PanelSection title="Endereço">
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-cep">CEP</label>
                                    <input id="og-cep" className={`rp-input ${cep.aviso?.erro ? 'has-error' : ''}`}
                                        placeholder="00000-000" inputMode="numeric" maxLength={9} {...campo('cep')} />
                                    {cep.aviso && <p className={cep.aviso.erro ? 'rp-error' : 'rp-hint'}>{cep.aviso.texto}</p>}
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-bairro">Bairro</label>
                                    <input id="og-bairro" className="rp-input is-auto" disabled={cep.travado('bairro')} {...campo('bairro')} />
                                </div>
                            </div>
                            <div className="rp-three">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-rua">Rua</label>
                                    <input id="og-rua" className="rp-input is-auto" disabled={cep.travado('logradouro')} {...campo('logradouro')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-compl">Complemento</label>
                                    <input id="og-compl" className="rp-input" {...campo('complemento')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-num">Nº</label>
                                    <input id="og-num" className="rp-input" {...campo('numero')} />
                                </div>
                            </div>
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-cidade">Cidade</label>
                                    <input id="og-cidade" className="rp-input is-auto" disabled={cep.travado('cidade')} {...campo('cidade')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="og-uf">UF</label>
                                    <input id="og-uf" className="rp-input is-auto" maxLength={2} disabled={cep.travado('estado')} {...campo('estado')} />
                                </div>
                            </div>
                        </PanelSection>

                        <PanelSection title="Observação">
                            <div className="rp-field">
                                <textarea id="og-obs" className="rp-input og-textarea" rows={3}
                                    placeholder="Como essa empresa compra, com quem falar, o que evitar…"
                                    {...campo('observacao')} />
                            </div>
                        </PanelSection>
                    </>
                )}

                {modo === 'ver' && aberto && aba === 'historico' && (
                    <SecaoHistorico entidade="organizacao" id={aberto.organizacaoId} />
                )}
            </RecordPanel>

            {podeGerenciar && (
                <ImportarPlanilhaPanel
                    open={importando}
                    onClose={() => setImportando(false)}
                    contexto="organizacoes"
                    onConcluido={carregar}
                />
            )}
        </div>
    );
};

export default OrganizationsPage;
